import {
  type CanvasElementFrom,
  type PropsOf,
  type WithProps,
  type WithSlots,
} from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputBackground,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { DrawerComponent } from './drawer.component';
import { drawerV2Migration } from './migrations/002-drawer.migration';

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    drawer: DrawerDef;
  }
}

export const DRAWER_CONTENT_SLOT = 'content';
export const DRAWER_COLUMN_WIDTH = 375;

const MIN_SIZE = 150;
const MAX_WIDTH = 1280;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed'];

export type DrawerDef = WithSlots<typeof DRAWER_CONTENT_SLOT> & WithProps<DrawerPropsModel>;
export type DrawerElement = CanvasElementFrom<'drawer'>;

export function getDrawerDefinition(): CanvasElementDefinition<'drawer'> {
  return {
    type: 'drawer',
    get componentType(): typeof DrawerComponent {
      return DrawerComponent;
    },
    isListed: false,
    name: 'Drawer',
    width: { mode: 'value', fixed: true, value: DRAWER_COLUMN_WIDTH },
    height: { mode: 'full' },
    propsConfig: getPropsConfig(),
    defaults: getDefaults(),
    slots: {
      ids: [DRAWER_CONTENT_SLOT],
      [DRAWER_CONTENT_SLOT]: {
        children: [],
        props: {
          style: {
            padding: '0px 0px 0px 0px',
          },
        },
      },
    },
    features: {
      resizeWithChild: true,
    },
    version: 2,
    migrations: {
      2: drawerV2Migration,
    },
  };
}

registerElementDefinition(getDrawerDefinition());

export type DrawerPropsModel = {
  style: {
    container: PropertiesConfigInputBackground & {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
  };
};

function getDefaults(): PropsOf<'drawer'> {
  return {
    style: {
      container: {
        offset: { padding: '0px' },
        outlineWidth: '1px',
        fill: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
        visualEffect: {
          type: 'drop-shadow',
          data: { x: '0', y: '4', blur: '24', color1: '#00000014' },
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('drawer', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content', 'Type', [
        epc.fields.radiosField(
          '$$page.type',
          'Type',
          [
            { label: 'Dialog', value: 'Dialog' },
            { label: 'Drawer', value: 'Drawer' },
          ],
          { hideLabel: true, renderType: 'text', stretched: true },
        ),
      ]),

      epc.subsections.subsection('content', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          fillLabel: 'Background',
          hasWidth: false,
          hasRadius: false,
          allowNoneFill: false,
          allowNoneOutline: false,
        }),
      ],
      true,
    ),
  ]);
}
